import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { Evento, TipoDocumento } from '@/lib/supabase/tipos-bd'

/**
 * Historia de un documento.
 *
 * Se lee de lo más viejo a lo más nuevo: es un relato, no una lista de
 * novedades. Quien abre la bitácora seis meses después quiere reconstruir qué
 * pasó, en el orden en que pasó.
 */
export function useBitacora(tipo: TipoDocumento, documentoId: string | undefined) {
  const consulta = useQuery({
    queryKey: ['bitacora', tipo, documentoId],
    enabled: Boolean(documentoId),
    queryFn: async (): Promise<Evento[]> => {
      // La de una orden de servicio es la de TODA su cadena (Johan:
      // «responsables por si pasa algo»): la orden, sus cotizaciones —también
      // las separadas— y sus órdenes de trabajo, en un solo relato.
      const ids = [documentoId!]
      if (tipo === 'orden_servicio') {
        const [cots, ots] = await Promise.all([
          supabase.from('cotizaciones').select('id').eq('orden_servicio_id', documentoId!),
          supabase.from('ordenes_trabajo').select('id').eq('orden_servicio_id', documentoId!),
        ])
        if (cots.error) throw new Error(mensajeDeError(cots.error))
        if (ots.error) throw new Error(mensajeDeError(ots.error))
        ids.push(...(cots.data ?? []).map((c) => c.id), ...(ots.data ?? []).map((o) => o.id))
      }

      const consulta = supabase.from('eventos').select('*')
      const { data, error } = await (tipo === 'orden_servicio'
        ? consulta.in('documento_id', ids)
        : consulta.eq('documento_tipo', tipo).eq('documento_id', documentoId!)
      ).order('ocurrido_en', { ascending: true })

      if (error) throw new Error(mensajeDeError(error))

      return data ?? []
    },
  })

  return {
    eventos: consulta.data ?? [],
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}
